// frontend/src/pages/LandingPage/AjudaPage.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    HelpCircle, 
    ChevronDown,
    ChevronUp,
    Search,
    Book,
    Video,
    FileText,
    MessageSquare,
    Mail,
    Phone,
    Clock,
    CheckCircle2,
    Users,
    Settings,
    CreditCard,
    Shield,
    Zap,
    ArrowRight
} from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { Link } from 'react-router-dom';

const AjudaPage = () => {
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    const [searchQuery, setSearchQuery] = useState('');
    const [openFaq, setOpenFaq] = useState(null);

    // Categorias de FAQ
    const faqCategories = [
        {
            id: 'inicio',
            title: 'Primeiros Passos',
            icon: Zap,
            color: 'blue',
            faqs: [
                {
                    question: 'Como criar minha conta no Higeia.tech?',
                    answer: 'Para criar sua conta, clique em "Solicitar Demonstração" na página inicial ou acesse a página de registro. Você precisará informar seus dados pessoais, dados da organização e escolher um plano. O processo é rápido e leva menos de 5 minutos.'
                },
                {
                    question: 'Preciso de algum conhecimento técnico para usar o sistema?',
                    answer: 'Não! O Higeia.tech foi desenvolvido para ser intuitivo e fácil de usar. Nossa interface é amigável e não requer conhecimento técnico. Além disso, oferecemos treinamentos e materiais de apoio para ajudar você e sua equipe.'
                },
                {
                    question: 'Posso testar o sistema antes de assinar?',
                    answer: 'Sim! Oferecemos um período de teste gratuito de 30 dias. Durante esse período, você terá acesso completo a todas as funcionalidades do plano escolhido, sem compromisso.'
                },
                {
                    question: 'Como faço para adicionar usuários à minha organização?',
                    answer: 'Após criar sua conta, acesse o menu "Usuários" no dashboard. Lá você pode adicionar novos usuários, definir seus perfis e permissões. Cada usuário receberá um e-mail com instruções para ativar sua conta.'
                }
            ]
        },
        {
            id: 'funcionalidades',
            title: 'Funcionalidades',
            icon: Settings,
            color: 'green',
            faqs: [
                {
                    question: 'Como funciona o prontuário eletrônico?',
                    answer: 'O prontuário eletrônico permite registrar todas as informações do paciente de forma digital e segura. Você pode adicionar anamnese, evoluções, exames, prescrições e muito mais. Todos os dados ficam organizados e acessíveis de qualquer lugar.'
                },
                {
                    question: 'O sistema gera relatórios automáticos?',
                    answer: 'Sim! O Higeia.tech gera diversos relatórios automáticos, incluindo relatórios de atendimentos, estoque, financeiro e de conformidade. Você também pode personalizar relatórios conforme suas necessidades.'
                },
                {
                    question: 'Como funciona o controle de ponto dos profissionais?',
                    answer: 'O sistema de check-in/check-out permite que os profissionais registrem sua entrada e saída diretamente pelo sistema. Você pode acompanhar horas trabalhadas, gerar relatórios de presença e configurar alertas de atraso.'
                },
                {
                    question: 'Posso agendar consultas e tarefas recorrentes?',
                    answer: 'Sim! O sistema permite criar agendamentos únicos ou recorrentes. Você pode agendar consultas, tarefas de cuidado, administração de medicamentos e muito mais, com lembretes automáticos.'
                },
                {
                    question: 'Como funciona a gestão de medicamentos?',
                    answer: 'O módulo de medicamentos permite controlar o estoque, receber alertas de validade, registrar administração de medicamentos aos pacientes e gerar relatórios de consumo. Tudo para garantir a segurança e eficiência.'
                }
            ]
        },
        {
            id: 'assinatura',
            title: 'Planos e Assinatura',
            icon: CreditCard,
            color: 'purple',
            faqs: [
                {
                    question: 'Quais são os planos disponíveis?',
                    answer: 'O plano Essencial custa R$ 29,00 por paciente. Acima de 20 pacientes, o valor cai para R$ 24,90 por paciente; acima de 30, para R$ 19,90; e acima de 40, para R$ 14,90. Os detalhes estão na página de Planos.'
                },
                {
                    question: 'Posso mudar de plano depois?',
                    answer: 'Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. As alterações serão aplicadas na próxima fatura e você terá acesso imediato às novas funcionalidades (no caso de upgrade).'
                },
                {
                    question: 'Como funciona o pagamento?',
                    answer: 'Aceitamos pagamento via cartão de crédito ou boleto bancário. O pagamento é processado mensalmente de forma automática. Você receberá uma fatura por e-mail antes de cada cobrança.'
                },
                {
                    question: 'O que acontece se eu cancelar minha assinatura?',
                    answer: 'Você pode cancelar sua assinatura a qualquer momento. Seu acesso permanecerá ativo até o final do período pago. Após o cancelamento, você poderá exportar seus dados antes do encerramento do acesso.'
                },
                {
                    question: 'Há taxa de cancelamento?',
                    answer: 'Não! Não cobramos taxa de cancelamento. Você pode cancelar quando quiser, sem multas ou taxas adicionais.'
                }
            ]
        },
        {
            id: 'seguranca',
            title: 'Segurança e Privacidade',
            icon: Shield,
            color: 'orange',
            faqs: [
                {
                    question: 'Meus dados estão seguros?',
                    answer: 'Sim! Utilizamos criptografia SSL de ponta a ponta, backups automáticos diários e servidores em data centers certificados. Todos os dados são protegidos conforme a LGPD (Lei Geral de Proteção de Dados).'
                },
                {
                    question: 'O sistema está em conformidade com a LGPD?',
                    answer: 'Sim! O Higeia.tech está totalmente em conformidade com a LGPD. Implementamos todas as medidas técnicas e organizacionais necessárias para garantir a proteção dos dados pessoais.'
                },
                {
                    question: 'Quem tem acesso aos dados dos pacientes?',
                    answer: 'Apenas usuários autorizados pela sua organização têm acesso aos dados. Você controla completamente quem pode ver e editar informações através do sistema de permissões (RBAC).'
                },
                {
                    question: 'Os dados são armazenados no Brasil?',
                    answer: 'Sim! Todos os dados são armazenados em servidores localizados no Brasil, garantindo conformidade total com a legislação brasileira de proteção de dados.'
                },
                {
                    question: 'Como funciona o backup dos dados?',
                    answer: 'Realizamos backups automáticos diários de todos os dados. Os backups são armazenados de forma segura e criptografada, permitindo recuperação rápida em caso de necessidade.'
                }
            ]
        },
        {
            id: 'suporte',
            title: 'Suporte e Contato',
            icon: MessageSquare,
            color: 'teal',
            faqs: [
                {
                    question: 'Como entro em contato com o suporte?',
                    answer: 'Você pode entrar em contato conosco através do e-mail contato@higeia.tech, telefone (53) 3028-3030, ou através do formulário na página de Contato. Clientes Premium têm acesso a suporte prioritário via WhatsApp.'
                },
                {
                    question: 'Qual o horário de atendimento?',
                    answer: 'Nosso horário de atendimento é de segunda a sexta, das 9h às 18h. Clientes Premium têm acesso a suporte 24/7 via WhatsApp.'
                },
                {
                    question: 'Oferecem treinamento para a equipe?',
                    answer: 'Sim! Oferecemos treinamentos personalizados para sua equipe, incluindo vídeos tutoriais, documentação completa e sessões de treinamento online ou presencial (conforme disponibilidade).'
                },
                {
                    question: 'Onde encontro tutoriais e documentação?',
                    answer: 'Você pode acessar nossa documentação completa dentro do sistema, além de vídeos tutoriais na seção de ajuda. Também enviamos materiais de apoio por e-mail.'
                }
            ]
        }
    ];

    // Recursos úteis
    const recursosUteis = [
        {
            icon: Book,
            title: 'Documentação Completa',
            description: 'Acesse guias detalhados sobre todas as funcionalidades',
            link: '/recursos',
            color: 'blue'
        },
        {
            icon: Video,
            title: 'Vídeos Tutoriais',
            description: 'Aprenda com vídeos passo a passo',
            link: '/demonstracao',
            color: 'green'
        },
        {
            icon: FileText,
            title: 'Base de Conhecimento',
            description: 'Encontre respostas para dúvidas comuns',
            link: '#',
            color: 'purple'
        },
        {
            icon: MessageSquare,
            title: 'Fale Conosco',
            description: 'Entre em contato com nossa equipe de suporte',
            link: '/contato',
            color: 'orange'
        }
    ];

    // Filtrar FAQs baseado na busca
    const filteredFaqs = faqCategories.map(category => ({
        ...category,
        faqs: category.faqs.filter(faq => 
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
    })).filter(category => category.faqs.length > 0);

    const toggleFaq = (categoryId, faqIndex) => {
        const key = `${categoryId}-${faqIndex}`;
        setOpenFaq(openFaq === key ? null : key);
    };

    const colorClasses = {
        blue: {
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            icon: 'text-blue-600',
            title: 'text-blue-900',
            button: 'bg-blue-600 hover:bg-blue-700'
        },
        green: {
            bg: 'bg-green-50',
            border: 'border-green-200',
            icon: 'text-green-600',
            title: 'text-green-900',
            button: 'bg-green-600 hover:bg-green-700'
        },
        purple: {
            bg: 'bg-purple-50',
            border: 'border-purple-200',
            icon: 'text-purple-600',
            title: 'text-purple-900',
            button: 'bg-purple-600 hover:bg-purple-700'
        },
        orange: {
            bg: 'bg-orange-50',
            border: 'border-orange-200',
            icon: 'text-orange-600',
            title: 'text-orange-900',
            button: 'bg-orange-600 hover:bg-orange-700'
        },
        teal: {
            bg: 'bg-teal-50',
            border: 'border-teal-200',
            icon: 'text-teal-600',
            title: 'text-teal-900',
            button: 'bg-teal-600 hover:bg-teal-700'
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main>
                {/* Hero Section */}
                <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative py-20 sm:py-24 bg-gradient-to-br from-higeia-dark via-higeia-medium to-higeia-dark text-white overflow-hidden"
                >
                    {/* Padrão decorativo */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>

                    <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6"
                        >
                            <HelpCircle className="h-10 w-10" />
                        </motion.div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
                            Central de{' '}
                            <span className="bg-gradient-to-r from-white to-higeia-light bg-clip-text text-transparent">
                                Ajuda
                            </span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
                            Encontre respostas para suas dúvidas e aprenda a usar todas as funcionalidades do Higeia.tech
                        </p>

                        {/* Barra de Busca */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            className="max-w-2xl mx-auto"
                        >
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Busque por palavras-chave..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg"
                                />
                            </div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* Recursos Úteis */}
                <section className="py-12 bg-white border-b border-gray-200">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {recursosUteis.map((recurso, index) => {
                                const Icon = recurso.icon;
                                const colors = colorClasses[recurso.color];
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1, duration: 0.5 }}
                                    >
                                        <Link to={recurso.link}>
                                            <div className={`group bg-white rounded-xl p-6 border-2 ${colors.border} hover:shadow-lg transition-all duration-300 cursor-pointer h-full`}>
                                                <div className={`inline-flex items-center justify-center w-12 h-12 ${colors.bg} rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                                    <Icon className={`h-6 w-6 ${colors.icon}`} />
                                                </div>
                                                <h3 className="font-bold text-gray-900 mb-2">{recurso.title}</h3>
                                                <p className="text-sm text-gray-600">{recurso.description}</p>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* FAQ por Categorias */}
                <section className="py-16 sm:py-20 bg-gray-50">
                    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                        {filteredFaqs.length === 0 && searchQuery ? (
                            <div className="text-center py-12">
                                <HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum resultado encontrado</h3>
                                <p className="text-gray-600 mb-6">Tente buscar com outras palavras-chave</p>
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="text-higeia-dark hover:text-higeia-medium font-semibold"
                                >
                                    Limpar busca
                                </button>
                            </div>
                        ) : (
                            filteredFaqs.map((category, categoryIndex) => {
                                const Icon = category.icon;
                                const colors = colorClasses[category.color];
                                
                                return (
                                    <motion.div
                                        key={category.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: categoryIndex * 0.1, duration: 0.6 }}
                                        className="mb-12"
                                    >
                                        {/* Cabeçalho da Categoria */}
                                        <div className="flex items-center gap-4 mb-6">
                                            <div className={`flex-shrink-0 w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
                                                <Icon className={`h-6 w-6 ${colors.icon}`} />
                                            </div>
                                            <h2 className={`text-2xl sm:text-3xl font-black ${colors.title}`}>
                                                {category.title}
                                            </h2>
                                        </div>

                                        {/* Lista de FAQs */}
                                        <div className="space-y-4">
                                            {category.faqs.map((faq, faqIndex) => {
                                                const isOpen = openFaq === `${category.id}-${faqIndex}`;
                                                return (
                                                    <motion.div
                                                        key={faqIndex}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        whileInView={{ opacity: 1, y: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: categoryIndex * 0.1 + faqIndex * 0.05 }}
                                                        className={`bg-white rounded-xl border-2 ${colors.border} overflow-hidden transition-all duration-300 ${isOpen ? 'shadow-lg' : 'shadow-sm hover:shadow-md'}`}
                                                    >
                                                        <button
                                                            onClick={() => toggleFaq(category.id, faqIndex)}
                                                            className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            <span className="font-bold text-gray-900 pr-4">
                                                                {faq.question}
                                                            </span>
                                                            {isOpen ? (
                                                                <ChevronUp className={`h-5 w-5 ${colors.icon} flex-shrink-0`} />
                                                            ) : (
                                                                <ChevronDown className={`h-5 w-5 ${colors.icon} flex-shrink-0`} />
                                                            )}
                                                        </button>
                                                        {isOpen && (
                                                            <motion.div
                                                                initial={{ opacity: 0, height: 0 }}
                                                                animate={{ opacity: 1, height: 'auto' }}
                                                                exit={{ opacity: 0, height: 0 }}
                                                                transition={{ duration: 0.3 }}
                                                                className="px-6 pb-5"
                                                            >
                                                                <p className="text-gray-700 leading-relaxed">
                                                                    {faq.answer}
                                                                </p>
                                                            </motion.div>
                                                        )}
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </motion.div>
                                );
                            })
                        )}
                    </div>
                </section>

                {/* Seção: Ainda Precisa de Ajuda? */}
                <section className="py-16 sm:py-20 bg-gradient-to-br from-higeia-dark to-higeia-medium">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                                <MessageSquare className="h-8 w-8 text-white" />
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
                                Ainda Precisa de{' '}
                                <span className="bg-gradient-to-r from-white to-higeia-light bg-clip-text text-transparent">
                                    Ajuda?
                                </span>
                            </h2>
                            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                                Nossa equipe está pronta para ajudar você. Entre em contato através dos canais abaixo.
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                    <Mail className="h-8 w-8 text-white mx-auto mb-4" />
                                    <h3 className="font-bold text-white mb-2">E-mail</h3>
                                    <a href="mailto:contato@higeia.tech" className="text-white/90 hover:text-white transition-colors">
                                        contato@higeia.tech
                                    </a>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                    <Phone className="h-8 w-8 text-white mx-auto mb-4" />
                                    <h3 className="font-bold text-white mb-2">Telefone</h3>
                                    <a href="tel:+553530283030" className="text-white/90 hover:text-white transition-colors">
                                        (53) 3028-3030
                                    </a>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                    <Clock className="h-8 w-8 text-white mx-auto mb-4" />
                                    <h3 className="font-bold text-white mb-2">Horário</h3>
                                    <p className="text-white/90">Seg-Sex: 9h às 18h</p>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/contato">
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <PrimaryButton className="!px-8 !py-4 !text-lg !font-bold !bg-white !text-higeia-dark hover:!bg-gray-100 !shadow-xl !border-0">
                                            Enviar Mensagem
                                            <ArrowRight className="h-5 w-5 ml-2" />
                                        </PrimaryButton>
                                    </motion.div>
                                </Link>
                                <Link to="/demonstracao">
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <PrimaryButton className="!px-8 !py-4 !text-lg !font-bold !bg-transparent !border-2 !border-white !text-white hover:!bg-white/10 !shadow-xl">
                                            Agendar Demonstração
                                        </PrimaryButton>
                                    </motion.div>
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default AjudaPage;

